import { z } from 'zod';

/**
 * Customer reviews shown on the storefront. Staff keep the full list in the admin (System > Reviews);
 * the storefront only shows reviews rated at least `minRating` stars (4 unless staff change it) that are not hidden.
 */
export const REVIEWS_MAX = 200;
export const REVIEW_BODY_MAX = 600;
export const REVIEW_AUTHOR_MAX = 80;
export const DEFAULT_MIN_RATING = 4;
/** How many qualifying reviews the home page row shows; the rest are on the All reviews page. */
export const HOME_REVIEWS_SHOWN = 9;

export const storedReviewSchema = z.object({
  /** "Priya, Bengaluru": the name, then the place */
  author: z.string().trim().min(1, 'Add a name').max(REVIEW_AUTHOR_MAX),
  body: z.string().trim().min(1, 'Add what they said').max(REVIEW_BODY_MAX, `Keep it under ${REVIEW_BODY_MAX} characters`),
  rating: z.number().int().min(1).max(5),
  /** kept in the list but not shown to customers */
  hidden: z.boolean().optional(),
});

export const reviewsAdminSchema = z.object({
  minRating: z.number().int().min(1).max(5),
  reviews: z.array(storedReviewSchema).max(REVIEWS_MAX, `At most ${REVIEWS_MAX} reviews`),
});

export type StoredReview = z.infer<typeof storedReviewSchema>;
export type ReviewsAdminInput = z.infer<typeof reviewsAdminSchema>;

/** What customers see. */
export interface ReviewDTO {
  author: string;
  body: string;
  rating: number;
}

/**
 * The average, count and star breakdown are over every review that is shown, not just the few on the home page.
 * `distribution[n]` is how many shown reviews gave n stars; `minRating` is the lowest star level customers can see.
 */
export interface ReviewSummaryDTO {
  count: number;
  average: number;
  distribution: Record<1 | 2 | 3 | 4 | 5, number>;
  minRating: number;
}

export const STAR_LEVELS = [5, 4, 3, 2, 1] as const;

/** Does this review appear on the storefront? */
export function isShown(review: Pick<StoredReview, 'rating' | 'hidden'>, minRating: number): boolean {
  return !review.hidden && review.rating >= minRating;
}
